import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { Platform } from 'react-native';

/**
 * Screen-share track carry across the pre-flight → exam room switch.
 *
 * Why this exists (2026-07-12): a proctored oral exam moves the student from the
 * shared pre-flight room into their per-booking exam room when the lecturer starts
 * the exam. That remounts `MeetingRoom` (`key={token}`) and `room.disconnect()`
 * STOPS every locally-published track — including the screen share the student
 * granted in the lobby. Re-acquiring screen share in the new room needs a fresh
 * `getDisplayMedia`, which the browser only allows from a user gesture. Silent
 * auto-publish therefore fails in real browsers (it "worked" only in the e2e
 * harness, which fakes `getDisplayMedia`), the integrity monitor pauses the exam,
 * and the lecturer sees no screen feed.
 *
 * Fix: capture the display MediaStream ONCE in the lobby (with the gesture), hold
 * the raw track in this context which lives ABOVE the room-remount boundary, and
 * republish a per-room CLONE of it. Stopping a clone on disconnect leaves the
 * source live, so the exam room republishes without any new gesture.
 */

export type CarriedScreenShare = {
  /** Capture the display surface with a user gesture and retain it. Web-only. */
  capture: () => Promise<boolean>;
  /** Stop the retained capture entirely (call when the session ends). */
  release: () => void;
  /**
   * A live clone of the retained display track for publishing into the current
   * room, or `null` when nothing is retained / the source has ended. Each call
   * returns a fresh clone so `room.disconnect(stopTracks)` never kills the source.
   */
  cloneTrack: () => MediaStreamTrack | null;
  /** True while a live display track is retained. */
  hasScreen: boolean;
};

const noop = (): void => {};

const CarriedScreenShareContext = createContext<CarriedScreenShare>({
  capture: async () => false,
  release: noop,
  cloneTrack: () => null,
  hasScreen: false,
});

function getDisplayMedia(): Promise<MediaStream> | null {
  if (Platform.OS !== 'web') return null;
  const md = (globalThis as { navigator?: { mediaDevices?: MediaDevices } }).navigator?.mediaDevices;
  if (!md || typeof md.getDisplayMedia !== 'function') return null;
  return md.getDisplayMedia({ video: true, audio: false });
}

function stopTracks(tracks: MediaStreamTrack[]): void {
  tracks.forEach((track) => {
    try {
      track.stop();
    } catch {
      // Already ended — ignore so wrap-up/leave cannot throw.
    }
  });
}

export function CarriedScreenShareProvider({ children }: { children: React.ReactNode }) {
  const streamRef = useRef<MediaStream | null>(null);
  const clonesRef = useRef<MediaStreamTrack[]>([]);
  const [hasScreen, setHasScreen] = useState(false);

  const release = useCallback(() => {
    // Chrome keeps the "sharing this tab" bar while ANY track from the
    // getDisplayMedia session is live — including clones published to LiveKit.
    // Stopping only the source leaves the OS/browser indicator up after wrap-up.
    // Drop refs first: source `ended` also calls release, and must not re-stop.
    const clones = clonesRef.current;
    clonesRef.current = [];
    const stream = streamRef.current;
    streamRef.current = null;
    setHasScreen(false);
    stopTracks(clones);
    if (stream) {
      stopTracks(stream.getTracks());
    }
  }, []);

  const capture = useCallback(async (): Promise<boolean> => {
    const request = getDisplayMedia();
    if (!request) return false;
    try {
      const stream = await request;
      // Replace any prior capture (e.g. the student re-picks a surface).
      if (streamRef.current) {
        stopTracks(clonesRef.current);
        clonesRef.current = [];
        stopTracks(streamRef.current.getTracks());
      }
      streamRef.current = stream;
      // When the user hits the browser's native "Stop sharing", drop the carry so
      // the integrity monitor sees the source as down instead of us handing out
      // clones of a dead track.
      stream.getVideoTracks().forEach((track) => {
        track.addEventListener('ended', () => {
          if (streamRef.current === stream) release();
        });
      });
      setHasScreen(stream.getVideoTracks().some((t) => t.readyState === 'live'));
      return stream.getVideoTracks().length > 0;
    } catch {
      // User cancelled the picker or permission denied — caller keeps the gate closed.
      return false;
    }
  }, [release]);

  const cloneTrack = useCallback((): MediaStreamTrack | null => {
    const track = streamRef.current?.getVideoTracks().find((t) => t.readyState === 'live');
    if (!track) return null;
    const clone = track.clone();
    clonesRef.current.push(clone);
    clone.addEventListener('ended', () => {
      clonesRef.current = clonesRef.current.filter((held) => held !== clone);
    });
    return clone;
  }, []);

  // Release the underlying capture when the whole session unmounts so the browser
  // sharing indicator never lingers after the student leaves.
  useEffect(() => () => { release(); }, [release]);

  const value = useMemo<CarriedScreenShare>(
    () => ({
      capture, release, cloneTrack, hasScreen,
    }),
    [capture, release, cloneTrack, hasScreen],
  );

  return (
    <CarriedScreenShareContext.Provider value={value}>
      {children}
    </CarriedScreenShareContext.Provider>
  );
}

export function useCarriedScreenShare(): CarriedScreenShare {
  return useContext(CarriedScreenShareContext);
}
